import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fitFontSize, followScrollTop } from '../fitText';

interface RevealTextProps {
  text: string;
  /** How far through the spoken audio we are, 0 to 1. Words appear in step. */
  progress: number;
  /** The design size for this message (any CSS length); the fit only shrinks it. */
  fontSize: string;
  /** Smallest size, in px, before the text scrolls instead of shrinking further. */
  minFontSize: number;
  /** Height the text may take, in px. Null until the caller has measured it. */
  room: number | null;
  className?: string;
  style?: React.CSSProperties;
}

interface Fit {
  text: string;
  fontSize: string;
  room: number;
  px: number;
  /** Doesn't fit even at minFontSize: the text scrolls with the voice. */
  overflow: boolean;
}

const ENTER_EASE = 'cubic-bezier(.22,.61,.36,1)';

/**
 * The interviewer's message, revealed word by word in time with the voice
 * (karaoke style), and sized so the whole message fits its box.
 *
 * Every word is laid out from the first frame (unrevealed words are only
 * transparent), so the size is fitted to the full message before it shows,
 * shrinking from the design size down to `minFontSize`. Only a message too long
 * even for that scrolls, keeping the spoken word in view.
 *
 * Once the reveal has settled, the per-word transitions and filters are dropped
 * so the browser releases their compositing layers (which otherwise render the
 * text with greyscale antialiasing). The words stay the same inline-blocks, so
 * the message keeps exactly the same line breaks and nothing jumps.
 */
const RevealText: React.FC<RevealTextProps> = ({
  text,
  progress,
  fontSize,
  minFontSize,
  room,
  className,
  style,
}) => {
  const pRef = useRef<HTMLParagraphElement | null>(null);

  const tokens = useMemo(() => (text ? text.split(/(\s+)/).filter(t => t.length > 0) : []), [text]);
  const wordCount = useMemo(() => tokens.filter(t => !/^\s+$/.test(t)).length, [tokens]);
  const wordsRevealed = Math.min(wordCount, Math.ceil(progress * wordCount + 0.2));

  // Settled per message, so a new message never shows whole for a frame
  // before its reveal starts.
  const allRevealed = wordCount > 0 && wordsRevealed >= wordCount;
  const [settledText, setSettledText] = useState<string | null>(null);
  useEffect(() => {
    if (!allRevealed) return;
    const t = setTimeout(() => setSettledText(text), 700);
    return () => clearTimeout(t);
  }, [allRevealed, text]);
  const settled = allRevealed && settledText === text;

  // Re-fit when the viewport resizes (wrapping changes with width) and once the
  // web font has loaded (its metrics differ from the fallback's).
  const [layoutTick, setLayoutTick] = useState(0);
  useEffect(() => {
    const bump = () => setLayoutTick(n => n + 1);
    window.addEventListener('resize', bump);
    let live = true;
    document.fonts?.ready.then(() => live && bump()).catch(() => {});
    return () => {
      live = false;
      window.removeEventListener('resize', bump);
    };
  }, []);

  const [fit, setFit] = useState<Fit | null>(null);
  const fitted = !!fit && fit.text === text && fit.fontSize === fontSize;
  const overflow = fitted && fit.overflow;

  useLayoutEffect(() => {
    const p = pRef.current;
    if (!p || room == null || !text) return;
    // The scroll fallback caps the paragraph's height; lift that while measuring.
    const { maxHeight, overflowY } = p.style;
    p.style.maxHeight = 'none';
    p.style.overflowY = 'visible';
    p.style.fontSize = fontSize;
    const max = parseFloat(getComputedStyle(p).fontSize);
    // offsetHeight is the laid-out height: the rise of words not yet revealed is
    // a transform, which layout ignores.
    const fits = (px: number) => {
      p.style.fontSize = `${px}px`;
      return p.offsetHeight <= room + 0.5 && p.scrollWidth <= p.clientWidth + 1;
    };
    const px = fitFontSize({ min: Math.min(minFontSize, max), max, fits });
    const next: Fit = { text, fontSize, room, px, overflow: !fits(px) };
    p.style.maxHeight = maxHeight;
    p.style.overflowY = overflowY;
    setFit(prev =>
      prev &&
      prev.text === next.text &&
      prev.fontSize === next.fontSize &&
      prev.px === next.px &&
      prev.overflow === next.overflow
        ? prev
        : next,
    );
  }, [text, fontSize, minFontSize, room, layoutTick]);

  // Scroll fallback: keep the spoken word's line mid-box, and fade the bottom
  // edge while more of the message waits below it.
  const [moreBelow, setMoreBelow] = useState(false);
  useLayoutEffect(() => {
    const p = pRef.current;
    if (!p || !overflow) {
      setMoreBelow(false);
      return;
    }
    const word = (n: number) => p.querySelector<HTMLElement>(`[data-word="${n}"]`);
    const last = word(wordCount - 1);
    const spoken = word(wordsRevealed - 1);
    const contentHeight = last ? last.offsetTop + last.offsetHeight : p.clientHeight;
    const top = spoken
      ? followScrollTop({
          clientHeight: p.clientHeight,
          contentHeight,
          wordTop: spoken.offsetTop,
          wordHeight: spoken.offsetHeight,
        })
      : 0;
    if (Math.abs(top - p.scrollTop) > 1) {
      const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      p.scrollTo({ top, behavior: calm ? 'auto' : 'smooth' });
    }
    setMoreBelow(top < contentHeight - p.clientHeight - 1);
  }, [overflow, wordsRevealed, wordCount, fit]);

  const px = fitted ? fit.px : null;
  const fade = 'linear-gradient(to bottom, #000 calc(100% - 22px), transparent 100%)';

  let wordIdx = 0;
  return (
    <p
      ref={pRef}
      className={[className, overflow ? 'lp-hide-scrollbar' : ''].filter(Boolean).join(' ')}
      style={{
        ...style,
        fontSize: px != null ? `${px}px` : fontSize,
        // Offset parent for the words, so their offsetTop is measured from here.
        position: 'relative',
        ...(overflow
          ? {
              maxHeight: room ?? undefined,
              overflowY: 'auto',
              ...(moreBelow ? { maskImage: fade, WebkitMaskImage: fade } : null),
            }
          : null),
      }}
    >
      {tokens.map((tok, i) => {
        if (/^\s+$/.test(tok)) return <span key={i}>{tok}</span>;
        const idx = wordIdx;
        wordIdx += 1;
        const isRevealed = idx < wordsRevealed;
        const stagger = Math.max(0, wordsRevealed - idx - 1) * 0.05;
        return (
          <span
            key={i}
            data-word={idx}
            style={
              settled
                ? { display: 'inline-block' }
                : {
                    display: 'inline-block',
                    opacity: isRevealed ? 1 : 0,
                    transform: isRevealed ? 'none' : 'translate3d(0, 10px, 0)',
                    filter: isRevealed ? 'none' : 'blur(8px)',
                    transition: `opacity 460ms ${ENTER_EASE}, transform 460ms ${ENTER_EASE}, filter 520ms ${ENTER_EASE}`,
                    transitionDelay: isRevealed ? `${stagger}s` : '0s',
                  }
            }
          >
            {tok}
          </span>
        );
      })}
    </p>
  );
};

export default RevealText;
